import { useCallback } from 'react'
import { Form } from 'react-final-form'

import { Button } from '@elements/button'
import { ErrorNote } from '@elements/error-note'
import { TextField } from '@components/form-field/text'
import { createValidator, validations } from '@lib/validation'
import { useSession } from '@hooks/session'
import { SignupFormValues } from 'types/model/auth'
import { TablelistLogo } from '@icons/tablelist-logo'

const signupValidator = createValidator<SignupFormValues>({
  firstName: validations.string().required('Please enter your first name'),
  lastName: validations.string().required('Please enter your last name'),
  email: validations
    .string()
    .required('Please enter your email address')
    .email('Please enter a valid email address'),
  password: validations
    .string()
    .required('Please enter a password')
    .trim()
    .min(8, 'Must be at least 8 characters long')
    .test(
      'validPassword',
      'Password must contain at least 1 capital, 1 number, and 1 special character',
      (val) =>
        /[A-Z]/gi.test(val) &&
        /[0-9]/gi.test(val) &&
        /[\~\`\!\@\#\$\%\^\&\*\(\)\-\_\+\=\{\}\[\]\|\\\;\:\"\<\>\,\.\/\?]/gi.test(val)
    ),
  confirmPassword: validations
    .string()
    .required('Please enter the same passowrd')
    .test(
      'matchingPassword',
      'Please enter the same password',
      (val, ctx) => val === ctx.parent.password
    ),
})

export function SignupForm({
  afterSignup,
  initialValues,
}: {
  afterSignup?: () => void
  initialValues?: Partial<SignupFormValues>
}) {
  const { signup, authError } = useSession()

  const submit = useCallback(
    async (values: SignupFormValues) => {
      const { firstName, lastName, email, password } = values
      const success = await signup({ firstName, lastName, email, password })
      if (success) afterSignup?.()
    },
    [afterSignup, signup]
  )

  return (
    <Form onSubmit={submit} validate={signupValidator} initialValues={initialValues}>
      {({ handleSubmit }) => (
        <form className="space-y-6 contents" onSubmit={handleSubmit}>
          <div className="space-y-3">
            <div className="flex items-start w-full space-x-3">
              <TextField
                placeholder="Enter your first name"
                label="First Name"
                name="firstName"
                autoComplete="given-name"
              />
              <TextField
                placeholder="Enter your last name"
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
              />
            </div>
            <TextField
              placeholder="Enter your email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
            />
            <TextField
              placeholder="Enter a valid password"
              label="Password"
              name="password"
              type="password"
              autoComplete="new-password"
            />
            <TextField
              placeholder="Enter the same password"
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              autoComplete="off"
            />
          </div>
          <Button.Submit className="w-full stroke-white">
            <div className="flex items-center space-x-2">
              <TablelistLogo fill="white" className="w-5 h-5" />
              <span>Signup</span>
            </div>
          </Button.Submit>
          <ErrorNote>{authError}</ErrorNote>
        </form>
      )}
    </Form>
  )
}
